import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Form, Button, Row, Col } from 'react-bootstrap';
import { getBankAccounts, updateBankAccountStatus, verifyBankAccount, deleteBankAccount } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const Banks = () => {
    const [loading, setLoading] = useState(true);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [summary, setSummary] = useState({
        accounts: {
            total: 0,
            verified: 0,
            unverified: 0,
            active: 0,
            inactive: 0,
            withCryptoWallet: 0
        },
        wallets: {
            totalBalance: 0,
            totalHoldAmount: 0,
            activeWallets: 0
        }
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    });
    const [filters, setFilters] = useState({
        status: '',
        search: '',
        verified: ''
    });

    useEffect(() => {
        fetchBankAccounts();
    }, [pagination.page, pagination.limit, filters]);

    const fetchBankAccounts = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(filters.status && { status: filters.status }),
                ...(filters.search && { search: filters.search }),
                ...(filters.verified && { verified: filters.verified })
            };

            const response = await getBankAccounts(params);
            
            if (response.success && response.data) {
                setBankAccounts(response.data.bankAccounts || response.data || []);
                setPagination(prev => ({
                    ...prev,
                    ...(response.data.pagination || {})
                }));
                setSummary(response.data.summary || summary);
            } else {
                setBankAccounts([]);
            }
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
            alert(error.message || 'Failed to fetch bank accounts');
            setBankAccounts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusUpdate = async (accountId, newStatus) => {
        const confirmed = window.confirm(
            `Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} this bank account?`
        );

        if (!confirmed) return;

        try {
            const response = await updateBankAccountStatus(accountId, { status: newStatus });

            if (response.success) {
                alert("Bank account status updated successfully");
                fetchBankAccounts();
            } else {
                throw new Error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            alert(error.message || 'Failed to update status');
        }
    };

    const handleVerify = async (accountId) => {
        const confirmed = window.confirm(
            "Are you sure you want to verify this bank account?"
        );

        if (!confirmed) return;

        try {
            const response = await verifyBankAccount(accountId, {
                verified: true
            });

            if (response.success) {
                alert("Bank account verified successfully");
                fetchBankAccounts();
            } else {
                throw new Error(response.message || 'Failed to verify account');
            }
        } catch (error) {
            console.error('Error verifying account:', error);
            alert(error.message || 'Failed to verify account');
        }
    };

    const handleDelete = async (accountId) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this bank account?"
        );

        if (!confirmed) return;

        try {
            const response = await deleteBankAccount(accountId);

            if (response.success) {
                alert("Bank account deleted successfully");
                fetchBankAccounts();
            } else {
                throw new Error(response.message || 'Failed to delete account');
            }
        } catch (error) {
            console.error('Error deleting account:', error);
            alert(error.message || 'Failed to delete account');
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'success',
            inactive: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const renderTableContent = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan="6" className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </td>
                </tr>
            );
        }

        if (!bankAccounts || bankAccounts.length === 0) {
            return (
                <tr>
                    <td colSpan="6" className="text-center">
                        <div className="d-flex justify-content-center align-items-center flex-column" style={{minHeight: '200px'}}>
                            <i className="fa fa-university fa-3x text-muted mb-3"></i>
                            <h4 className="text-muted">No bank accounts found</h4>
                        </div>
                    </td>
                </tr>
            );
        }

        return bankAccounts.map((account) => (
            <tr key={account._id || account.id}>
                <td>
                    <div className="d-flex align-items-center">
                        <div>
                            <strong>{account.accountHolderName || '-'}</strong>
                            <div className="text-muted small">
                                {account.user?.name || account.userId?.name || 'N/A'} ({account.user?.email || account.userId?.email || 'N/A'})
                            </div>
                        </div>
                    </div>
                </td>
                <td>
                    <div>
                        <strong>{account.bankName || '-'}</strong>
                        <div className="text-muted small">
                            A/C: {account.accountNumber || '-'}
                            <br />
                            IFSC: {account.ifscCode || account.ifsc || '-'}
                        </div>
                    </div>
                </td>
                <td>
                    {account.walletAddress ? (
                        <div>
                            <div className="text-truncate" style={{maxWidth: '150px'}}>
                                {account.walletAddress}
                            </div>
                            <div className="text-muted small">
                                {account.walletName || '-'} ({account.networkType || '-'})
                            </div>
                        </div>
                    ) : (
                        <span className="text-muted">No wallet linked</span>
                    )}
                </td>
                <td>
                    <div className="d-flex flex-column gap-1">
                        <Badge bg={getStatusBadge(account.status)}>
                            {account.status || '-'}
                        </Badge>
                        {account.isPrimary && (
                            <Badge bg="info">Primary</Badge>
                        )}
                        <Badge bg={account.isVerified ? 'success' : 'warning'}>
                            {account.isVerified ? 'Verified' : 'Unverified'}
                        </Badge>
                    </div>
                </td>
                <td>{account.createdAt ? new Date(account.createdAt).toLocaleString() : '-'}</td>
                <td>
                    <div className="d-flex gap-2">
                        {!account.isVerified && (
                            <Button
                                variant="success"
                                size="sm"
                                className="btn-icon-xxs"
                                onClick={() => handleVerify(account._id || account.id)}
                                title="Verify Account"
                            >
                                <i className="fa fa-check"></i>
                            </Button>
                        )}
                        <Button
                            variant={account.status === 'active' ? 'warning' : 'success'}
                            size="sm"
                            className="btn-icon-xxs"
                            onClick={() => handleStatusUpdate(account._id || account.id, account.status === 'active' ? 'inactive' : 'active')}
                            title={account.status === 'active' ? 'Deactivate' : 'Activate'}
                        >
                            <i className={`fa fa-${account.status === 'active' ? 'ban' : 'check'}`}></i>
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            className="btn-icon-xxs"
                            onClick={() => handleDelete(account._id || account.id)}
                            title="Delete"
                        >
                            <i className="fa fa-trash"></i>
                        </Button>
                    </div>
                </td>
            </tr>
        ));
    };

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Bank Accounts" sidebutton={false} />

            <div className="row">
                <div className="col-xl-12">
                    <div className="row mb-4">
                        <div className="col-xl-3 col-sm-6 mb-3">
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h4 className="fs-18 font-w600 mb-1">{summary.accounts.total || 0}</h4>
                                        <span>Total Accounts</span>
                                    </div>
                                    <div className="d-inline-block position-relative donut-chart-sale">
                                        <i className="fa fa-university fa-2x text-primary"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-xl-3 col-sm-6 mb-3">
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h4 className="fs-18 font-w600 mb-1">{summary.accounts.verified || 0}</h4>
                                        <span>Verified Accounts</span>
                                    </div>
                                    <div className="d-inline-block position-relative donut-chart-sale">
                                        <i className="fa fa-check-circle fa-2x text-success"></i>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    <Card className="border-0 shadow-sm">
                        <Card.Header>
                            <Card.Title>Bank Accounts</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4">
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={filters.status}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Verification</Form.Label>
                                            <Form.Select
                                                name="verified"
                                                value={filters.verified}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All</option>
                                                <option value="true">Verified</option>
                                                <option value="false">Unverified</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Search</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="search"
                                                value={filters.search}
                                                onChange={handleFilterChange}
                                                placeholder="Search accounts..."
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>

                            <div className="table-responsive">
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th><strong>Account Holder</strong></th>
                                            <th><strong>Bank Details</strong></th>
                                            <th><strong>Wallet Details</strong></th>
                                            <th><strong>Status</strong></th>
                                            <th><strong>Created At</strong></th>
                                            <th><strong>Actions</strong></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderTableContent()}
                                    </tbody>
                                </Table>
                            </div>

                            {!loading && bankAccounts && bankAccounts.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                    </div>
                                    <div>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            className="me-2" 
                                            disabled={pagination.page === 1}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        >
                                            Previous
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            disabled={pagination.page === pagination.pages}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <style>{`
                .btn-icon-xxs {
                    width: 26px;
                    height: 26px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
            `}</style>
        </div>
    );
};

export default Banks;
